// src/components/modals/EditCustomerModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, User, Phone, Calendar, Building, ChevronDown, Star } from 'lucide-react'
import CustomerService from '../../services/customerService'
import BranchService from '../../services/branchService'
import { CustomerModel } from '../../utils/customerModel'

const EditCustomerModal = ({ isOpen, onClose, customer, onCustomerUpdated, restrictedBranchId = null, isBranchRestricted = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    branch: '',
    dateOfBirth: '',
    customerType: 'normal',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)

  // Initialize form data when modal opens or customer changes
  useEffect(() => {
    if (isOpen && customer) {
      const branchValue = isBranchRestricted && restrictedBranchId 
        ? restrictedBranchId 
        : (customer.branch?._id || customer.branch || '')
      
      setFormData({
        name: customer.name || '',
        mobileNumber: CustomerModel.convertMobileForFormInput(customer.mobileNumber || ''),
        branch: branchValue,
        dateOfBirth: customer.dateOfBirth ? customer.dateOfBirth.split('T')[0] : '',
        customerType: customer.customerType || 'normal',
        isActive: customer.isActive !== false
      })
      setErrors({})
      fetchBranches()
    }
  }, [isOpen, customer, isBranchRestricted, restrictedBranchId])

  // Fetch branches for dropdown
  const fetchBranches = async () => {
    try {
      setBranchesLoading(true)
      const response = await BranchService.getAllBranches()
      
      if (response?.success) {
        const branchesData = response.data?.branches || []
        let activeBranches = branchesData.filter(branch => branch.isActive)
        
        // Filter to only manager's branch if restricted
        if (isBranchRestricted && restrictedBranchId) {
          activeBranches = activeBranches.filter(branch => branch._id === restrictedBranchId)
          console.log('🔒 Filtered to manager\'s branch only for customer edit')
        }
        
        setBranches(activeBranches)
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
    } finally {
      setBranchesLoading(false)
    }
  }

  // Validate mobile number (exactly 10 digits)
  const validateMobileNumber = (mobile) => {
    const mobileRegex = /^[0-9]{10}$/
    return mobileRegex.test(mobile)
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'mobileNumber') {
      // For mobile number, only allow digits and limit to 10
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required'
    }

    // Mobile number validation
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required'
    } else if (!validateMobileNumber(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must be exactly 10 digits'
    }

    // Branch validation
    if (!formData.branch) {
      newErrors.branch = 'Branch selection is required'
    }

    // Date of birth validation (optional now)

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      const customerData = CustomerService.formatCustomerForAPI(formData)
      const response = await CustomerService.updateCustomer(customer._id, customerData)
      
      if (response?.success) {
        console.log('✅ Customer updated successfully:', response.data)
        
        if (onCustomerUpdated) {
          onCustomerUpdated(response.data)
        }
        
        onClose()
      } else {
        setErrors({ general: response?.message || 'Failed to update customer' })
      }
    } catch (error) {
      console.error('❌ Error updating customer:', error)
      
      if (error.response?.status === 409) {
        setErrors({ mobileNumber: 'A customer with this mobile number already exists' })
      } else if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || error.message
        if (errorMessage.toLowerCase().includes('mobile')) {
          setErrors({ mobileNumber: errorMessage })
        } else if (errorMessage.toLowerCase().includes('name')) {
          setErrors({ name: errorMessage })
        } else {
          setErrors({ general: errorMessage })
        }
      } else if (error.response?.status === 404) {
        setErrors({ general: 'Customer not found' })
      } else {
        setErrors({ general: error.message || 'Failed to update customer' })
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !customer) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            {/* <div className="p-2 bg-orange-100 rounded-lg">
              <User className="h-5 w-5 text-orange-600" />
            </div> */}
            <h2 className="text-xl font-semibold text-gray-900">Edit Customer</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2  "
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name *
            </label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter customer name"
              className={errors.name ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="0778289336"
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.mobileNumber ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                disabled={loading}
                maxLength="10"
              />
            </div>
            {errors.mobileNumber && <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>}
          </div>

          {/* Branch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-4 w-4 text-gray-400" />
              </div>
              <select
                name="branch"
                value={formData.branch}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white appearance-none ${
                  errors.branch ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                } ${isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                disabled={loading || branchesLoading || isBranchRestricted}
              >
                <option value="">Select branch</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
            {errors.branch && <p className="mt-1 text-sm text-red-600">{errors.branch}</p>}
            {branchesLoading && <p className="mt-1 text-sm text-gray-500">Loading branches...</p>}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.dateOfBirth ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                disabled={loading}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
          </div>

          {/* Customer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Type *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({ ...prev, customerType: 'normal' }))
                  if (errors.customerType) {
                    setErrors(prev => ({ ...prev, customerType: '' }))
                  }
                }}
                disabled={loading}
                className={`h-12 rounded-lg font-medium text-base transition-all flex items-center justify-center space-x-2 ${
                  formData.customerType === 'normal'
                    ? 'bg-orange-500 text-white border-2 border-orange-500 shadow-md'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-orange-300'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Normal</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({ ...prev, customerType: 'vip' }))
                  if (errors.customerType) {
                    setErrors(prev => ({ ...prev, customerType: '' }))
                  }
                }}
                disabled={loading}
                className={`h-12 rounded-lg font-medium text-base transition-all flex items-center justify-center space-x-2 ${
                  formData.customerType === 'vip'
                    ? 'bg-gradient-to-br from-yellow-50 to-amber-50 text-amber-700 border-2 border-amber-300 shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-amber-300'
                }`}
              >
                <Star className="h-4 w-4 fill-current" />
                <span>VIP</span>
              </button>
            </div>
            {errors.customerType && <p className="mt-1 text-sm text-red-600">{errors.customerType}</p>}
          </div>

          {/* 
          {/* Status Switch */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Status
                </label>
                {/* <p className="text-xs text-gray-500">
                  {formData.isActive ? 'Customer is active and can place orders' : 'Customer is inactive and hidden'}
                </p> */}
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {/* <span className="ml-3 text-sm text-gray-700">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span> */}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Customer'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditCustomerModal